// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';
import myw from 'myWorld/base/core';
import 'myWorld/base/control';

/**
 * Options for FieldViewer instances
 * @name FieldViewer
 * @typedef fieldViewerOptions
 * @property {boolean}  [inListView=false]      Whether self is embeded in a list of multiple of features or not. Affects how units and null values are displayed
 * @property {boolean}  [renderAll=false]       When false, null and default values are not rendered
 */

myw.FieldViewer = myw.Control.extend({
    options: {
        inListView: false,
        renderAll: false
    },

    /**
     * @class Control to display a feature's field value. <br/>
     *        Super-class with the common behaviour for the sub-classes that deal with specific field types <br/>
     *        Provided sub-classes are: <br/>
     *        {@link ReferenceFieldViewer} <br/>
     *        {@link ReferenceSetFieldViewer} <br/>
     *        {@link TimeFieldViewer} <br/>
     *        {@link LinkFieldViewer} <br/>
     *        {@link ImageFieldViewer} <br/>
     *        {@link NumberFieldViewer} <br/>
     * @param  {Control}            owner   Control where self will be included
     * @param  {Feature}            feature
     * @param  {fieldDD}                fieldDD
     * @param  {fieldViewerOptions}     options
     * @constructs
     * @extends {Control}
     */
    initialize(owner, feature, fieldDD, options) {
        myw.Control.prototype.initialize.call(this, owner, options);

        this.feature = feature;
        this.fieldDD = fieldDD;
        this.fieldName = fieldDD.internal_name;
        this.fieldValue = this.feature.properties[this.fieldName];
        this.displayValue = this.feature.displayValues[this.fieldName];
        this.error = this.displayValue instanceof myw.Error && this.displayValue;
        if (this.error) this.displayValue = myw.msg('errors', this.error.name);

        this.render();
    },

    /**
     * Renders the field value in self's element
     */
    render() {
        const fieldValue = this.fieldValue;
        const fieldDefault = this.fieldDD['default'];
        const nully = fieldValue === null || fieldValue === '';
        const isDefault = fieldDefault && fieldValue == fieldDefault;

        //first check if we should render the value or not
        if (!this.options.renderAll) {
            if (nully) return;
            if (isDefault && !this.options.inListView) return;
        }

        //we do need to render the value anyway
        if (nully) {
            this.$el.html(`<i>${this.msg('null_value')}</i>`);
        } else {
            this.renderValue(fieldValue);
        }

        if (this.options.inListView) {
            this.$el.addClass('limited-width');
        } else {
            if (this.isLongString()) {
                //The field editor used for long string fields is a textarea which allows new lines
                //Hence the fieldViewer must respect new lines as well
                this.$el.addClass('allow-new-lines');
            }
        }
    },

    /**
     * Converts a value to an appropriate string to display to the user and
     * sets it as the content of self's element
     */
    renderValue(fieldValue) {
        let displayValue;
        try {
            displayValue = this.convertValue(fieldValue);
        } catch (e) {
            console.log(`Unable to convert value for field '${this.fieldName}'. Exception:`, e);
        }
        this.$el.html(displayValue);
    },

    /**
     * Returns true is the field is a string with max char limit > 100
     * @return {boolean}
     */
    isLongString() {
        let isLongString = false;
        const fieldType = this.fieldDD.type;
        const isString = fieldType.split('(')[0] === 'string';

        if (isString) {
            const lengthStr = fieldType
                .split('(')
                .pop()
                .slice(0, -1);
            if (parseInt(lengthStr, 10) > 100) {
                isLongString = true;
            }
        }
        return isLongString;
    },

    /**
     * Converts the value for display <br/>
     * Escapes the value and adds unit information where required
     * @return {string} Value as a string
     */
    convertValue(value) {
        let str;
        if (this.displayValue) {
            str = _.escape(this.displayValue);
        } else {
            str = _.escape(value);
            if (this.fieldDD.unit && !this.options.inListView) {
                str = `${str}&nbsp;${this.fieldDD.unit}`;
            } else if (this.fieldDD.enum) {
                //Use the display value
                const enumVal = this.fieldDD.enumValues.find(item => item.value === value);
                str = enumVal ? enumVal.display_value : value;
            }
        }

        return str;
    }
});

export default myw.FieldViewer;
